import React from "react";

import { formatAmount } from "src/utils/number";
import BalanceStats from "src/components/Modals/CashFlows/BalanceStats";

import {
  Container,
  Wrapper,
  Header,
  Body,
  List,
  Item,
  Text,
  NumberCell,
} from "./style";

export const ValueDetails = ({
  balance,
  reserved,
  /** What the form is taking out, so the strip can state what is left. */
  amount,
  /** The dialog's form: one strip of stats, as the deposit dialog states its
      own three readings. */
  asDialog,
  /** A refresh is in flight over figures that are already on screen. */
  updating,
}: {
  balance?: number;
  reserved?: number;
  amount?: number;
  asDialog?: boolean;
  updating?: boolean;
}) => {
  const cash = Number(balance ?? 0);
  const held = Number(reserved ?? 0);
  const taking = Number(amount ?? 0);

  /* The sign precedes the currency: −$476, never $-476. An amount larger
     than the balance is rejected by the form, but while it is being typed
     this cell states the subtraction, and it should state it in the
     product's own idiom. */
  const money = (value: number) =>
    `${value < 0 ? "−" : ""}$${formatAmount(Math.abs(value))}`;

  if (asDialog)
    return (
      <BalanceStats
        updating={updating}
        readings={[
          { label: "Total cash", value: money(cash) },
          /* Unsigned. In the page's panel these three were a SUM — total,
             less reserved, equals available — and the minus was the
             arithmetic. Across the strip they are four independent readings
             of the account, and a signed one there reads as money going out
             rather than money held back. */
          { label: "Reserved", value: money(held) },
          { label: "Available to withdraw", value: money(cash - held) },
          /* A projection, and only while there is something to project. With
             no amount entered — or none that can be — it was restating total
             cash under a different name, which reads as a second balance
             rather than as the result of the withdrawal. "-" is what the
             tables print for a figure that does not exist yet. */
          {
            label: "New balance",
            value: taking > 0 ? money(cash - taking) : "-",
          },
        ]}
      />
    );

  return (
    <Container>
      <Wrapper>
        <Header>Account balance</Header>
        <Body>
          <List>
            <Item>
              <Text>Total cash</Text>
              <NumberCell>${formatAmount(balance)}</NumberCell>
            </Item>
            {/* Signed, because it is the term being taken away — and second,
                because the answer below it is the subtraction. */}
            <Item>
              <Text>Reserved</Text>
              <NumberCell>−${formatAmount(reserved)}</NumberCell>
            </Item>
            <Item $total>
              <Text>Available to withdraw</Text>
              <NumberCell isBlue={true} $total>
                ${formatAmount(balance - reserved)}
              </NumberCell>
            </Item>
          </List>
        </Body>
      </Wrapper>
    </Container>
  );
};
